export const FAST_GLOBAL_EVENT_SCHEMA = 'fast_global_event_v1';
export const FAST_GLOBAL_BATCH_EVENT_SCHEMA = 'fast_global_batch_event_v1';
export const COMPARISON_EVENT_SCHEMA = 'comparison_stream_event_v1';
export const COMPARISON_EVENT_SCHEMA_V2 = 'comparison_stream_event_v2';
export const REFERENCE_REVIEW_EVENT_SCHEMA = 'reference_review_event_v1';

export const isReferenceReviewStreamEvent = (payload) => (
    payload?.schema_version === REFERENCE_REVIEW_EVENT_SCHEMA
    || payload?.review_mode === 'reference_review'
    || ['reference_requirement_progress', 'reference_verdict_progress'].includes(payload?.event)
);

export const isFastGlobalStreamEvent = (payload) => (
    [FAST_GLOBAL_EVENT_SCHEMA, FAST_GLOBAL_BATCH_EVENT_SCHEMA].includes(payload?.schema_version)
);

export const isComparisonStreamEvent = (payload) => (
    [COMPARISON_EVENT_SCHEMA, COMPARISON_EVENT_SCHEMA_V2].includes(payload?.schema_version)
    || payload?.review_mode === 'comparison'
);

export const isStructuredProgressStreamEvent = (payload) => (
    isFastGlobalStreamEvent(payload) || isComparisonStreamEvent(payload) || isReferenceReviewStreamEvent(payload)
);

const cleanText = (value) => (
    typeof value === 'string'
        ? value.replace(/\s+/g, ' ').trim()
        : ''
);

const OBSERVATION_STAGES = new Set([
    'contract_reading',
    'causal_analysis',
    'exposure',
    'interaction',
    'validation',
]);
const OBSERVATION_KINDS = new Set([
    'clause',
    'mechanism',
    'exposure',
    'connection',
    'validation',
]);

const cleanTextList = (value, limit = 5) => (
    Array.isArray(value)
        ? value.map(cleanText).filter(Boolean).slice(0, limit)
        : []
);

const normalizeCausalChain = (value) => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
    const trigger = cleanText(value.trigger);
    const consequence = cleanText(value.consequence);
    if (!trigger || !consequence) return null;
    return {
        trigger,
        rule_steps: cleanTextList(value.rule_steps, 7),
        consequence,
    };
};

export const normalizeFastGlobalObservation = (payload) => {
    if (!payload || payload.event !== 'analysis_observation' || !isStructuredProgressStreamEvent(payload)) {
        return null;
    }

    const source = payload.data && typeof payload.data === 'object'
        ? payload.data
        : payload;
    const observationId = cleanText(source.observation_id);
    if (!observationId || source.provisional !== true) return null;

    let headline = cleanText(source.headline);
    let message = cleanText(source.message);
    const summary = typeof source.summary === 'string'
        ? source.summary.trim()
        : '';
    const hasReadableSummary = Boolean(summary)
        && !(summary.startsWith('{') && summary.endsWith('}'))
        && !(summary.startsWith('[') && summary.endsWith(']'));
    if ((!headline || !message) && hasReadableSummary) {
        const [firstLine, ...remainingLines] = source.summary
            .split(/\r?\n/)
            .map(cleanText)
            .filter(Boolean);
        headline = headline || firstLine || '';
        message = message || remainingLines.join(' ');
    }
    if (!headline && !message) {
        return null;
    }
    if (!headline) {
        headline = message;
    } else if (!message) {
        message = headline;
    }
    if (!headline || !message) return null;

    const evidenceIds = cleanTextList(source.evidence_ids || source.evidence_source_ids, 20);
    const comparisonState = cleanText(source.comparison_state);
    const comparisonFields = comparisonState
        ? {
            comparison_state: comparisonState,
            assessment_status: cleanText(source.assessment_status) || null,
            assessment: cleanText(source.assessment) || null,
            severity: cleanText(source.severity) || null,
            verification: cleanText(source.verification) || null,
            partial: source.partial === true,
            change_type: cleanText(source.change_type) || null,
        }
        : {};
    return {
        observation_id: observationId,
        provisional: true,
        stage: OBSERVATION_STAGES.has(source.stage) ? source.stage : 'validation',
        kind: OBSERVATION_KINDS.has(source.kind) ? source.kind : 'validation',
        headline,
        message,
        causal_chain: normalizeCausalChain(source.causal_chain),
        aggravations: cleanTextList(source.aggravations),
        uncertainties: cleanTextList(source.uncertainties, 4),
        evidence_ids: evidenceIds,
        evidence_count: evidenceIds.length,
        ...comparisonFields,
    };
};

export const normalizeFastGlobalThinking = (payload) => {
    if (!payload || payload.event !== 'analysis_thinking' || !isFastGlobalStreamEvent(payload)) {
        return null;
    }
    const source = payload.data && typeof payload.data === 'object'
        ? payload.data
        : payload;
    const thinkingId = cleanText(source.thinking_id);
    const headline = cleanText(source.headline);
    const message = cleanText(source.message);
    const itemIndex = Number(source.item_index);
    if (
        !thinkingId
        || !headline
        || !message
        || !(
            source.provisional === undefined
            || source.provisional === null
            || source.provisional === true
            || source.provisional === "true"
            || source.provisional === 1
        )
        || !Number.isInteger(itemIndex)
        || itemIndex < 0
    ) return null;
    return {
        thinking_id: thinkingId,
        criterion_id: cleanText(source.criterion_id),
        item_index: itemIndex,
        headline,
        message,
        complete: source.complete === true,
    };
};

export const readAnalysisTerminal = (payload) => {
    const data = payload?.data && typeof payload.data === 'object' ? payload.data : {};
    const rawCompletionStatus = payload?.completion_status || data.completion_status || null;
    const completionStatus = rawCompletionStatus === 'complete' ? 'completed' : rawCompletionStatus;
    const outcomeCode = payload?.outcome_code
        || data.outcome_code
        || payload?.result?.outcome_code
        || data?.result?.outcome_code
        || null;
    const ok = payload?.ok === true || data.ok === true;
    const referenceCompleted = payload?.review_mode === 'reference_review'
        && completionStatus === 'completed'
        && payload?.ok !== false && data.ok !== false;
    const businessPartial = completionStatus === 'partial'
        && ['partial', 'partial_no_publishable_cards'].includes(outcomeCode);
    const technicalFailure = completionStatus === 'failed'
        || completionStatus === 'cancelled'
        || (!businessPartial && !referenceCompleted && ok === false);
    return {
        ok,
        completionStatus,
        outcomeCode,
        businessPartial,
        technicalFailure,
        successful: !technicalFailure && outcomeCode !== 'partial_no_publishable_cards'
            && (completionStatus === 'partial'
                || referenceCompleted
                || (ok && ['completed', null].includes(completionStatus))),
    };
};

export const normalizeFastGlobalCitationBbox = (value) => {
    const source = Array.isArray(value) && value.length === 4
        ? {
            left: value[0],
            top: value[1],
            width: value[2],
            height: value[3],
        }
        : (value && typeof value === 'object' ? value : null);
    if (!source) return null;
    const bbox = {
        left: Number(source.left ?? source.Left ?? source.x),
        top: Number(source.top ?? source.Top ?? source.y),
        width: Number(source.width ?? source.Width),
        height: Number(source.height ?? source.Height),
    };
    if (
        !Object.values(bbox).every(Number.isFinite)
        || bbox.width <= 0
        || bbox.height <= 0
    ) return null;
    return bbox;
};

export const hydrateFastGlobalCitationAnchor = (payload = {}) => {
    let metadata = payload.metadata_json ?? payload.metadataJson ?? null;
    if (typeof metadata === 'string') {
        try {
            metadata = JSON.parse(metadata);
        } catch (_) {
            metadata = null;
        }
    }
    const rawCitations = Array.isArray(payload.citations)
        ? payload.citations
        : (Array.isArray(metadata?.citations) ? metadata.citations : []);
    const citations = rawCitations
        .filter((citation) => citation && typeof citation === 'object')
        .map((citation) => ({
            ...citation,
            bbox: citation.resolution_status && citation.resolution_status !== 'resolved'
                ? null : normalizeFastGlobalCitationBbox(citation.bbox),
        }));
    const anchor = citations.find((citation) => citation.bbox) || citations[0] || {};
    const bbox = normalizeFastGlobalCitationBbox(payload.bbox) || anchor.bbox || null;
    return {
        ref: payload.ref && payload.ref !== 'global' ? payload.ref : anchor.citation_id || null,
        page: payload.page || anchor.page || null,
        file_id: payload.file_id || anchor.file_id || null,
        quote: payload.quote || anchor.excerpt || null,
        bbox,
        bbox_left: bbox?.left ?? null,
        bbox_top: bbox?.top ?? null,
        bbox_width: bbox?.width ?? null,
        bbox_height: bbox?.height ?? null,
        citations,
    };
};

export const adaptFastGlobalCommentCard = (payload) => {
    if (
        !payload
        || payload.event !== 'comment_card'
        || !isFastGlobalStreamEvent(payload)
    ) {
        return payload;
    }
    const citationAnchor = hydrateFastGlobalCitationAnchor(payload);
    const metadata = payload.metadata_json && typeof payload.metadata_json === 'object'
        ? payload.metadata_json
        : {};
    const primaryCriterionId = cleanText(
        payload.primary_criterion_id || payload.criterion_id
    );
    const matchedCriteria = Array.isArray(payload.matched_criteria)
        ? payload.matched_criteria
        : [];
    const primaryCriterion = matchedCriteria.find(
        (criterion) => cleanText(criterion?.criterion_id) === primaryCriterionId
    ) || (matchedCriteria.length === 1 ? matchedCriteria[0] : null);
    const criterionTitle = cleanText(payload.criterion_title)
        || cleanText(primaryCriterion?.title);
    return {
        ...payload,
        ...(criterionTitle ? { criterion_title: criterionTitle } : {}),
        id: payload.id || payload.card_id,
        metadata_json: {
            ...metadata,
            ...(citationAnchor.citations.length ? {citations: citationAnchor.citations} : {}),
            review_mode: 'fast_global',
            fast_global_card_id: payload.card_id || payload.id,
            source_review_method: payload.source_review_method || metadata.source_review_method,
            priority_rank: payload.priority_rank ?? metadata.priority_rank ?? null,
            priority_reason: payload.priority_reason ?? metadata.priority_reason ?? null,
            priority_status: payload.priority_status ?? metadata.priority_status ?? null,
            ...(Array.isArray(payload.uncertainties) ? {
                fast_global_card_v1: { ...metadata.fast_global_card_v1, uncertainties: payload.uncertainties },
            } : {}),
            final_for_run: payload.final_for_run === true,
            snapshot_persisted: payload.snapshot_persisted === true,
            review_annotations: payload.review_annotations || metadata.review_annotations || [],
        },
        ...citationAnchor,
        risk: payload.risk || payload.risk_level,
        suggested_action: payload.suggested_action || payload.recommendation || '',
        quick_view: payload.quick_view || {
            risk: payload.description || payload.title || '',
            action: payload.recommendation || '',
            description: payload.description || '',
        },
    };
};
